import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { updatePlayer } from '../services/players';
import { changeLanguage } from '../utils/i18n';
import { getLicenseInfo } from '../services/licenses';
import { supabase } from '../lib/supabase';
import DeleteAccountModal from '../components/DeleteAccountModal';
import './Profile.css';

const Profile: React.FC = () => {
  const { t } = useTranslation();
  const { player, user, loadPlayer, deleteAccount } = useAuth();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: player?.name || '',
    preName: player?.preName || '',
    pin: player?.pin || '',
    language: player?.language || 'de',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [licenseInfo, setLicenseInfo] = useState<any>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null);

  // Update form data when player data changes
  useEffect(() => {
    if (player) {
      setFormData({
        name: player.name || '',
        preName: player.preName || '',
        pin: player.pin || '',
        language: player.language || 'de',
      });
    }
  }, [player]);

  // Load profile picture
  useEffect(() => {
    if (player?.profile_picture_url) {
      setProfilePictureUrl(player.profile_picture_url);
    }
  }, [player]);

  // Load license information
  useEffect(() => {
    const loadLicenseInfo = async () => {
      try {
        const license = await getLicenseInfo();
        setLicenseInfo(license);
      } catch (err) {
        console.error('Failed to load license info:', err);
      }
    };
    
    loadLicenseInfo();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!player) return;

    setLoading(true);
    setError(null);

    try {
      await updatePlayer(player.id, formData);
      
      // Update language if it changed
      if (formData.language !== player?.language) {
        await changeLanguage(formData.language);
      }
      
      // Refresh player data from the database
      if (user) {
        await loadPlayer(user.id);
      }
      
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.failedToUpdateProfile'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleCancel = () => {
    setFormData({
      name: player?.name || '',
      preName: player?.preName || '',
      pin: player?.pin || '',
      language: player?.language || 'de',
    });
    setEditing(false);
    setError(null);
  };

  const handleDeleteAccount = async () => {
    try {
      await deleteAccount();
      // User will be signed out and redirected by the AuthContext
    } catch (err) {
      setError(err instanceof Error ? err.message : t('profile.deleteAccountError'));
      throw err; // Re-throw to let the modal handle the error display
    }
  };

  const handlePictureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0] || !player || !user) return;

    const file = e.target.files[0];
    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}.${fileExt}`;
    const filePath = `${fileName}`;

    try {
      setUploadingPicture(true);
      setError(null);

      console.log('📤 Upload Picture:');
      console.log('  File:', file.name);
      console.log('  Path:', filePath);

      // Delete old picture if exists
      if (player.profile_picture_url) {
        const oldPath = player.profile_picture_url.split('/').pop();
        if (oldPath) {
          console.log('  Deleting old:', oldPath);
          await supabase.storage.from('profile-pictures').remove([oldPath]);
        }
      }

      // Upload new picture
      const { error: uploadError } = await supabase.storage
        .from('profile-pictures')
        .upload(filePath, file, { upsert: true });

      if (uploadError) {
        console.error('❌ Upload error:', uploadError);
        throw uploadError;
      }

      console.log('✅ File uploaded successfully');

      // Get public URL
      const { data } = supabase.storage
        .from('profile-pictures')
        .getPublicUrl(filePath);

      console.log('🔗 Public URL:', data.publicUrl);

      // Update player profile with new URL
      console.log('💾 Updating player profile...');
      const result = await updatePlayer(player.id, {
        profile_picture_url: data.publicUrl
      });

      console.log('✅ Player profile updated:', result.profile_picture_url);

      setProfilePictureUrl(data.publicUrl);

      // Reload player data
      if (user) {
        await loadPlayer(user.id);
      }
    } catch (err) {
      console.error('❌ Upload failed:', err);
      setError(err instanceof Error ? err.message : t('profile.pictureUploadError'));
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleRemovePicture = async () => {
    if (!player || !user || !player.profile_picture_url) return;

    try {
      setUploadingPicture(true);
      setError(null);

      // DEBUG: Log current state
      console.log('🔍 Debug Remove Picture:');
      console.log('  Player ID:', player.id);
      console.log('  Player UUID:', player.playerUUID);
      console.log('  User ID (auth.uid):', user.id);
      console.log('  Match?', user.id === player.playerUUID);
      console.log('  deleted_at:', player.deleted_at);
      console.log('  is_guest:', player.is_guest);

      // Extract filename from URL - handle both full URLs and relative paths
      const urlParts = player.profile_picture_url.split('/');
      const fileName = urlParts[urlParts.length - 1];
      
      console.log('📁 Storage deletion:');
      console.log('  Full URL:', player.profile_picture_url);
      console.log('  Extracted filename:', fileName);
      
      if (fileName) {
        // Delete from storage
        const { data: deleteData, error: storageError } = await supabase.storage
          .from('profile-pictures')
          .remove([fileName]);
        
        console.log('  Storage delete result:', deleteData);
        console.log('  Storage delete error:', storageError);
        
        if (storageError) {
          console.warn('⚠️  Storage deletion error (may not exist):', storageError);
          // Continue anyway - the file might already be deleted
        } else {
          console.log('✅ Storage file deleted successfully');
        }
      }

      // Update player profile (remove the URL reference)
      console.log('🔄 Attempting updatePlayer with:', {
        id: player.id,
        update: { profile_picture_url: null }
      });
      
      const result = await updatePlayer(player.id, {
        profile_picture_url: null
      });
      
      console.log('✅ Update successful:', result);
      console.log('  profile_picture_url in result:', result.profile_picture_url);
      console.log('  Is it null/undefined?', result.profile_picture_url === null || result.profile_picture_url === undefined);

      setProfilePictureUrl(null);

      // Reload player data
      if (user) {
        await loadPlayer(user.id);
      }
    } catch (err) {
      console.error('❌ Error removing picture:', err);
      setError(err instanceof Error ? err.message : t('profile.pictureRemoveError'));
    } finally {
      setUploadingPicture(false);
    }
  };

  if (!player) {
    return (
      <div className="profile-container">
        <div className="error-message">
          <p>{t('profile.profileNotFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1>{t('profile.title')}</h1>
        {!editing && (
          <button onClick={() => setEditing(true)} className="btn btn-primary">
            {t('profile.editProfile')}
          </button>
        )}
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="profile-card">
        {/* Profile Picture Section */}
        <div className="profile-picture-section">
          <div className="avatar-container">
            {profilePictureUrl ? (
              <img src={profilePictureUrl} alt="Profile" className="profile-avatar" />
            ) : (
              <div className="profile-avatar-placeholder">
                <span>{player?.preName?.[0] || player?.name?.[0] || '?'}</span>
              </div>
            )}
          </div>
          <div className="avatar-actions">
            <input
              type="file"
              id="profile-picture-upload"
              accept="image/*"
              onChange={handlePictureUpload}
              style={{ display: 'none' }}
              disabled={uploadingPicture}
            />
            <label
              htmlFor="profile-picture-upload"
              className={`btn btn-secondary ${uploadingPicture ? 'disabled' : ''}`}
            >
              {uploadingPicture
                ? t('profile.uploadingPicture')
                : profilePictureUrl
                ? t('profile.changePicture')
                : t('profile.uploadPicture')}
            </label>
            {profilePictureUrl && (
              <button
                onClick={handleRemovePicture}
                className="btn btn-text"
                disabled={uploadingPicture}
              >
                {t('profile.removePicture')}
              </button>
            )}
          </div>
        </div>

        <div className="profile-info">
          <div className="info-item">
            <label>Email</label>
            <span>{user?.email}</span>
          </div>

          {editing ? (
            <form onSubmit={handleSubmit} className="profile-form">
              <div className="form-group">
                <label htmlFor="name">{t('profile.lastName')}</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="preName">{t('profile.firstName')}</label>
                <input
                  type="text"
                  id="preName"
                  name="preName"
                  value={formData.preName}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="pin">{t('profile.pin')}</label>
                <input
                  type="text"
                  id="pin"
                  name="pin"
                  value={formData.pin}
                  onChange={handleChange}
                  disabled={loading}
                  maxLength={4}
                />
              </div>

              <div className="form-group">
                <label htmlFor="language">{t('profile.language')}</label>
                <select
                  id="language"
                  name="language"
                  value={formData.language}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="de">Deutsch</option>
                  <option value="en">English</option>
                </select>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn btn-secondary"
                  disabled={loading}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? t('profile.saving') : t('profile.saveChanges')}
                </button>
              </div>
            </form>
          ) : (
            <>
              <div className="info-item">
                <label>Name</label>
                <span>{player.preName} {player.name}</span>
              </div>
              <div className="info-item">
                <label>{t('profile.pin')}</label>
                <span>{player.pin ? '****' : t('profile.notSet')}</span>
              </div>
              <div className="info-item">
                <label>{t('profile.language')}</label>
                <span>{player.language === 'en' ? 'English' : 'Deutsch'}</span>
              </div>
              
              {/* License Information */}
              {licenseInfo && (
                <div className="license-section">
                  <div className="license-header">
                    <label>{t('profile.currentLicense')}</label>
                    <span className={`license-badge license-${licenseInfo.licenseType}`}>
                      {t(`clubs.licenses.${licenseInfo.licenseType}`)}
                    </span>
                  </div>
                  
                  <div className="license-features">
                    <h4>{t('profile.licenseFeatures')}</h4>
                    <div className="features-grid">
                      <div className="feature-item">
                        <span className={`feature-icon ${licenseInfo.features.canCreateClubs ? 'enabled' : 'disabled'}`}>
                          {licenseInfo.features.canCreateClubs ? '✓' : '✗'}
                        </span>
                        <span className="feature-text">{t('clubs.licenses.features.canCreateClubs')}</span>
                      </div>
                      
                      <div className="feature-item">
                        <span className={`feature-icon ${licenseInfo.features.canCreateTournaments ? 'enabled' : 'disabled'}`}>
                          {licenseInfo.features.canCreateTournaments ? '✓' : '✗'}
                        </span>
                        <span className="feature-text">{t('clubs.licenses.features.canCreateTournaments')}</span>
                      </div>
                      
                      <div className="feature-item">
                        <span className={`feature-icon ${licenseInfo.features.canInviteMembers ? 'enabled' : 'disabled'}`}>
                          {licenseInfo.features.canInviteMembers ? '✓' : '✗'}
                        </span>
                        <span className="feature-text">{t('clubs.licenses.features.canInviteMembers')}</span>
                      </div>
                      
                      <div className="feature-item">
                        <span className={`feature-icon ${licenseInfo.features.analytics ? 'enabled' : 'disabled'}`}>
                          {licenseInfo.features.analytics ? '✓' : '✗'}
                        </span>
                        <span className="feature-text">{t('clubs.licenses.features.analytics')}</span>
                      </div>
                      
                      <div className="feature-item">
                        <span className={`feature-icon ${licenseInfo.features.customBranding ? 'enabled' : 'disabled'}`}>
                          {licenseInfo.features.customBranding ? '✓' : '✗'}
                        </span>
                        <span className="feature-text">{t('clubs.licenses.features.customBranding')}</span>
                      </div>
                    </div>
                  </div>
                  
                  {licenseInfo.expiresAt && (
                    <div className="license-expiry">
                      <label>{t('profile.licenseExpires')}</label>
                      <span>{new Date(licenseInfo.expiresAt).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete Account Section */}
      <div className="delete-account-section">
        <div className="delete-account-header">
          <h3>{t('profile.dangerZone')}</h3>
          <p>{t('profile.dangerZoneDescription')}</p>
        </div>
        <button
          onClick={() => setShowDeleteModal(true)}
          className="btn btn-danger"
        >
          {t('profile.deleteAccount')}
        </button>
      </div>

      {/* Delete Account Modal */}
      <DeleteAccountModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteAccount}
        userEmail={user?.email}
      />
    </div>
  );
};

export default Profile;

